import React, { useEffect, useMemo, useRef, useState } from "react";
import useSondageReponse from "../../../hooks/useSondageReponse";
import useSondageQuestion from "../../../hooks/useSondageQuestion";
import "../../../assets/css/sondage/AdminSondageReponse.css";
import ExcelJS from "exceljs";

const AdminSondageReponse = () => {
  const {
    getSondageDashboard,
    getReponseByUser,
    createSondageReponse,
    updateSondageReponse,
  } = useSondageReponse();

  const { getAllSondageQuestions } = useSondageQuestion();

  const [board, setBoard] = useState({ aFaire: [], fait: [] });
  const [questions, setQuestions] = useState({
    questionPourVendeur: [],
    questionPourArticle: [],
  });
  const [loading, setLoading] = useState(false);

  // ============================================================
  // MODAL
  // ============================================================

  const [selectedCard, setSelectedCard] = useState(null); // { type: "aFaire" | "fait", data }
  const [existingReponse, setExistingReponse] = useState(null);
  const [modalLoading, setModalLoading] = useState(false);

  const [savingKey, setSavingKey] = useState(null);
  const saving = savingKey !== null;

  const [activeSection, setActiveSection] = useState("all");

  const [answersVendeur, setAnswersVendeur] = useState({});
  const [answersArticles, setAnswersArticles] = useState({});

  // ---- remarques ----
  const [remarqueVendeur, setRemarqueVendeur] = useState("");
  const [remarquesArticles, setRemarquesArticles] = useState({}); // { articleId: string }

  // ---- questions imprévues ----
  const [extraQuestionsVendeur, setExtraQuestionsVendeur] = useState([]); // [{id, question, reponse}]
  const [extraQuestionsArticles, setExtraQuestionsArticles] = useState({}); // { articleId: [{id, question, reponse}] }

  const [bulkMode, setBulkMode] = useState(false);
  const [bulkSelectedIds, setBulkSelectedIds] = useState([]);
  const [bulkAnswers, setBulkAnswers] = useState({}); // { questionIndex: value }

  // ---- édition des réponses existantes ----
  const [editingVendeur, setEditingVendeur] = useState(false);
  const [editVendeurValues, setEditVendeurValues] = useState({});

  const [editingArticleId, setEditingArticleId] = useState(null);
  const [editArticleValues, setEditArticleValues] = useState({});
  const [mobileView, setMobileView] = useState("aFaire");

  const toggleBulkArticle = (articleId) => {
    setBulkSelectedIds((prev) =>
      prev.includes(articleId)
        ? prev.filter((id) => id !== articleId)
        : [...prev, articleId],
    );
  };

  const applyBulkAnswers = () => {
    bulkSelectedIds.forEach((articleId) => {
      Object.entries(bulkAnswers).forEach(([idx, value]) => {
        if (String(value || "").trim()) {
          setReponseArticle(articleId, idx, value);
        }
      });
    });
    setBulkMode(false);
    setBulkSelectedIds([]);
    setBulkAnswers({});
  };

  const modalContentRef = useRef(null);
  const [exporting, setExporting] = useState(false);
  const [search, setSearch] = useState("");

  const handleExportClick = async () => {
    if (exporting) return;
    setExporting(true);
    try {
      await exportFaitToExcel();
    } catch (err) {
      console.error(err);
      alert("Erreur lors de l'export Excel.");
    } finally {
      setExporting(false);
    }
  };

  // ============================================================
  // RECHERCHE (filtre les deux colonnes)
  // ============================================================

  const matchSearch = (u, articles = []) => {
    const nq = search.trim().toLowerCase();
    if (!nq) return true;

    const champs = [
      u?.nom,
      u?.prenom,
      u?.email,
      u?.tel,
      ...(articles || []).map((a) => a?.titre),
    ];

    return champs.some((champ) =>
      String(champ || "")
        .toLowerCase()
        .includes(nq),
    );
  };

  const filteredAFaire = useMemo(
    () =>
      board.aFaire.filter((item) =>
        matchSearch(item.utilisateur, item.articles),
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [board.aFaire, search],
  );

  const filteredFait = useMemo(
    () =>
      board.fait.filter((reponse) =>
        matchSearch(
          reponse.idUser,
          (reponse.reponseArticle || []).map((r) => r.idArticle),
        ),
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [board.fait, search],
  );

  // ============================================================
  // CHARGEMENT
  // ============================================================

  const loadBoard = async () => {
    setLoading(true);
    try {
      const res = await getSondageDashboard();
      setBoard({
        aFaire: res?.data?.data?.aFaire || [],
        fait: res?.data?.data?.fait || [],
      });
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const loadQuestions = async () => {
    try {
      const res = await getAllSondageQuestions();
      const list = res?.data?.data || [];
      if (list.length > 0) {
        setQuestions({
          questionPourVendeur: list[0]?.questionPourVendeur || [],
          questionPourArticle: list[0]?.questionPourArticle || [],
        });
      }
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    loadBoard();
    loadQuestions();
  }, []);

  // ============================================================
  // EXPORT EXCEL STYLISÉ (utilisateurs en lignes, questions en colonnes)
  // ============================================================

  const exportFaitToExcel = async () => {
    if (board.fait.length === 0) return;

    // 1. Liste ordonnée des questions uniques
    const questionsOrdonnees = [];
    const vuQuestions = new Set();

    board.fait.forEach((reponse) => {
      (reponse.reponseVendeur || []).forEach((qa) => {
        if (!vuQuestions.has(qa.question)) {
          vuQuestions.add(qa.question);
          questionsOrdonnees.push(qa.question);
        }
      });
      (reponse.reponseArticle || []).forEach((entry) => {
        (entry.reponses || []).forEach((qa) => {
          if (!vuQuestions.has(qa.question)) {
            vuQuestions.add(qa.question);
            questionsOrdonnees.push(qa.question);
          }
        });
      });
    });

    // 2. Une ligne par utilisateur
    const lignesUtilisateurs = board.fait.map((reponse) => {
      const u = reponse.idUser;
      const nomComplet = `${u?.nom || ""} ${u?.prenom || ""}`.trim() || "—";

      const reponsesParQuestion = {};
      const ajouterReponse = (question, valeur) => {
        if (!valeur) return;
        reponsesParQuestion[question] = reponsesParQuestion[question]
          ? `${reponsesParQuestion[question]}; ${valeur}`
          : valeur;
      };

      (reponse.reponseVendeur || []).forEach((qa) =>
        ajouterReponse(qa.question, qa.reponse),
      );
      (reponse.reponseArticle || []).forEach((entry) => {
        (entry.reponses || []).forEach((qa) =>
          ajouterReponse(qa.question, qa.reponse),
        );
      });

      return { nomComplet, reponsesParQuestion };
    });

    // 3. Création du classeur
    const workbook = new ExcelJS.Workbook();
    workbook.creator = "Sondages";
    workbook.created = new Date();

    const sheet = workbook.addWorksheet("Sondages faits", {
      views: [{ state: "frozen", xSplit: 1, ySplit: 1 }], // fige colonne Utilisateur + ligne d'en-tête
    });

    // 4. Colonnes (largeurs adaptatives selon le contenu)
    const colUtilisateur = {
      header: "Utilisateur",
      key: "utilisateur",
      width: 26,
    };
    const colsQuestions = questionsOrdonnees.map((q, idx) => ({
      header: q,
      key: `q${idx}`,
      width: Math.min(Math.max(q.length * 0.9, 22), 45),
    }));

    sheet.columns = [colUtilisateur, ...colsQuestions];

    // 5. Ligne d'en-tête stylée
    const headerRow = sheet.getRow(1);
    headerRow.height = 28;
    headerRow.eachCell((cell) => {
      cell.font = { bold: true, color: { argb: "FFFFFFFF" }, size: 11 };
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FF0B00CF" }, // couleur principale du projet
      };
      cell.alignment = {
        vertical: "middle",
        horizontal: "center",
        wrapText: true,
      };
      cell.border = {
        top: { style: "thin", color: { argb: "FFCCCCCC" } },
        bottom: { style: "thin", color: { argb: "FFCCCCCC" } },
        left: { style: "thin", color: { argb: "FFCCCCCC" } },
        right: { style: "thin", color: { argb: "FFCCCCCC" } },
      };
    });

    // 6. Lignes de données
    lignesUtilisateurs.forEach((ligne, i) => {
      const rowData = {
        utilisateur: ligne.nomComplet,
      };
      questionsOrdonnees.forEach((q, idx) => {
        rowData[`q${idx}`] = ligne.reponsesParQuestion[q] || "";
      });

      const row = sheet.addRow(rowData);
      row.alignment = { vertical: "top", wrapText: true };

      // Bandes alternées pour la lisibilité
      const isEven = i % 2 === 0;
      row.eachCell((cell, colNumber) => {
        cell.border = {
          top: { style: "thin", color: { argb: "FFEDEDED" } },
          bottom: { style: "thin", color: { argb: "FFEDEDED" } },
          left: { style: "thin", color: { argb: "FFEDEDED" } },
          right: { style: "thin", color: { argb: "FFEDEDED" } },
        };
        cell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: isEven ? "FFF7F7FB" : "FFFFFFFF" },
        };
        // Colonne "Utilisateur" en gras
        if (colNumber === 1) {
          cell.font = { bold: true };
        }
      });
    });

    // 7. Auto-filtre sur l'en-tête
    sheet.autoFilter = {
      from: { row: 1, column: 1 },
      to: { row: 1, column: questionsOrdonnees.length + 1 },
    };

    // 8. Génération et téléchargement du fichier
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    const dateStr = new Date().toISOString().slice(0, 10);
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `sondages_faits_${dateStr}.xlsx`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };
  // ============================================================
  // HELPERS AFFICHAGE
  // ============================================================

  const getInitiales = (u) =>
    `${(u?.nom || "").charAt(0)}${(u?.prenom || "").charAt(0)}`.toUpperCase() ||
    "?";

  // Priorité au numéro de l'utilisateur ; si absent, on retombe sur le
  // numéro (`phone`) de la première annonce liée qui en possède un.
  const getTelAffiche = (user, articles = []) => {
    if (user?.tel) return user.tel;
    const articleAvecTel = (articles || []).find((a) => a?.phone);
    return articleAvecTel?.phone || null;
  };

  const formatRelative = (date) => {
    if (!date) return "";
    const diffJours = Math.floor(
      (Date.now() - new Date(date).getTime()) / (1000 * 60 * 60 * 24),
    );

    if (diffJours <= 0) return "Aujourd'hui";
    if (diffJours === 1) return "Hier";
    if (diffJours < 30) return `Il y a ${diffJours} j`;
    const mois = Math.floor(diffJours / 30);
    if (mois < 12) return `Il y a ${mois} mois`;
    const ans = Math.floor(mois / 12);
    return `Il y a ${ans} an${ans > 1 ? "s" : ""}`;
  };

  const formatDate = (date) =>
    date ? new Date(date).toLocaleDateString("fr-FR") : "—";

  const genId = () =>
    `extra-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;

  // ============================================================
  // OUVERTURE DE CARTE
  // ============================================================

  const currentUserId =
    selectedCard?.type === "fait"
      ? selectedCard?.data?.idUser?._id
      : selectedCard?.data?.utilisateur?._id;

  const needsVendorSurvey =
    selectedCard?.type === "aFaire" &&
    (existingReponse?.reponseVendeur || []).length === 0 &&
    Number(selectedCard?.data?.utilisateur?.sondageFait) !== 1;

  const articlesEnAttente =
    selectedCard?.type === "aFaire"
      ? (selectedCard?.data?.articles || []).filter(
        (article) =>
          !(existingReponse?.reponseArticle || []).some(
            (r) =>
              String(r?.idArticle?._id || r?.idArticle) ===
              String(article._id),
          ),
      )
      : [];

  const openCard = async (type, item) => {
    setSelectedCard({ type, data: item });
    setAnswersVendeur({});
    setAnswersArticles({});
    setRemarqueVendeur("");
    setRemarquesArticles({});
    setExtraQuestionsVendeur([]);
    setExtraQuestionsArticles({});
    setActiveSection("all");
    setEditingVendeur(false);
    setEditVendeurValues({});
    setEditingArticleId(null);
    setEditArticleValues({});

    if (type === "fait") {
      setExistingReponse(item);
      return;
    }

    setModalLoading(true);
    try {
      const res = await getReponseByUser(item.utilisateur._id);
      setExistingReponse(res?.data?.data || null);
    } catch (err) {
      console.error(err);
      setExistingReponse(null);
    } finally {
      setModalLoading(false);
    }
  };

  const closeModal = () => {
    if (saving) return;
    setSelectedCard(null);
    setExistingReponse(null);
    setAnswersVendeur({});
    setAnswersArticles({});
    setRemarqueVendeur("");
    setRemarquesArticles({});
    setExtraQuestionsVendeur([]);
    setExtraQuestionsArticles({});
    setActiveSection("all");
    setEditingVendeur(false);
    setEditVendeurValues({});
    setEditingArticleId(null);
    setEditArticleValues({});
  };

  useEffect(() => {
    modalContentRef.current?.scrollTo({ top: 0, behavior: "smooth" });
  }, [activeSection]);

  // ============================================================
  // FORMULAIRES — réponses standards
  // ============================================================

  const setReponseVendeur = (index, value) => {
    setAnswersVendeur((prev) => ({ ...prev, [index]: value }));
  };

  const setReponseArticle = (articleId, index, value) => {
    setAnswersArticles((prev) => ({
      ...prev,
      [articleId]: { ...(prev[articleId] || {}), [index]: value },
    }));
  };

  // ============================================================
  // QUESTIONS IMPREVUES
  // ============================================================

  const addExtraQuestionVendeur = () => {
    setExtraQuestionsVendeur((prev) => [
      ...prev,
      { id: genId(), question: "", reponse: "" },
    ]);
  };

  const updateExtraQuestionVendeur = (id, field, value) => {
    setExtraQuestionsVendeur((prev) =>
      prev.map((q) => (q.id === id ? { ...q, [field]: value } : q)),
    );
  };

  const removeExtraQuestionVendeur = (id) => {
    setExtraQuestionsVendeur((prev) => prev.filter((q) => q.id !== id));
  };

  const addExtraQuestionArticle = (articleId) => {
    setExtraQuestionsArticles((prev) => ({
      ...prev,
      [articleId]: [
        ...(prev[articleId] || []),
        { id: genId(), question: "", reponse: "" },
      ],
    }));
  };

  const updateExtraQuestionArticle = (articleId, id, field, value) => {
    setExtraQuestionsArticles((prev) => ({
      ...prev,
      [articleId]: (prev[articleId] || []).map((q) =>
        q.id === id ? { ...q, [field]: value } : q,
      ),
    }));
  };

  const removeExtraQuestionArticle = (articleId, id) => {
    setExtraQuestionsArticles((prev) => ({
      ...prev,
      [articleId]: (prev[articleId] || []).filter((q) => q.id !== id),
    }));
  };

  // ============================================================
  // COMPLETUDE
  // ============================================================

  const vendeurComplete = useMemo(
    () =>
      questions.questionPourVendeur.length > 0 &&
      questions.questionPourVendeur.every(
        (_, idx) => String(answersVendeur[idx] || "").trim().length > 0,
      ),
    [questions, answersVendeur],
  );

  const isArticleComplete = (articleId) =>
    questions.questionPourArticle.length > 0 &&
    questions.questionPourArticle.every(
      (_, idx) =>
        String(answersArticles?.[articleId]?.[idx] || "").trim().length > 0,
    );

  const isFormComplete = useMemo(() => {
    if (needsVendorSurvey && !vendeurComplete) return false;
    return articlesEnAttente.every((article) => isArticleComplete(article._id));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    needsVendorSurvey,
    vendeurComplete,
    articlesEnAttente,
    answersArticles,
    questions,
  ]);

  // ============================================================
  // SOUMISSION (par section ou globale)
  // ============================================================

  const buildPayload = (includeVendeur, articleIdsToSubmit) => {
    const extraVendeurValid = extraQuestionsVendeur.filter(
      (q) => q.question.trim().length > 0 && q.reponse.trim().length > 0,
    );

    const nouveauReponseVendeur = includeVendeur
      ? [
        ...questions.questionPourVendeur.map((q, idx) => ({
          question: q.question,
          reponse: answersVendeur[idx] || "",
          typeReponse: Number(q.typeReponse) || 1,
          choix: q.choix || [],
        })),
        ...extraVendeurValid.map((q) => ({
          question: q.question,
          reponse: q.reponse,
          typeReponse: 1, // question imprévue = toujours texte libre
          choix: [],
        })),
      ]
      : existingReponse?.reponseVendeur || [];

    const nouvelleRemarqueVendeur = includeVendeur
      ? remarqueVendeur
      : existingReponse?.remarqueVendeur || "";

    const articlesToSubmit = articlesEnAttente.filter((a) =>
      articleIdsToSubmit.includes(a._id),
    );

    const nouveauxReponseArticle = articlesToSubmit.map((article) => {
      const extraValid = (extraQuestionsArticles[article._id] || []).filter(
        (q) => q.question.trim().length > 0 && q.reponse.trim().length > 0,
      );

      return {
        idArticle: article._id,
        reponses: [
          ...questions.questionPourArticle.map((q, idx) => ({
            question: q.question,
            reponse: answersArticles?.[article._id]?.[idx] || "",
            typeReponse: Number(q.typeReponse) || 1,
            choix: q.choix || [],
          })),
          ...extraValid.map((q) => ({
            question: q.question,
            reponse: q.reponse,
            typeReponse: 1,
            choix: [],
          })),
        ],
        remarque: remarquesArticles[article._id] || "",
      };
    });

    return {
      nouveauReponseVendeur,
      nouvelleRemarqueVendeur,
      nouveauxReponseArticle,
    };
  };

  const submitSection = async (key, { includeVendeur, articleIds }) => {
    if (saving) return;
    setSavingKey(key);

    try {
      const {
        nouveauReponseVendeur,
        nouvelleRemarqueVendeur,
        nouveauxReponseArticle,
      } = buildPayload(includeVendeur, articleIds);

      if (existingReponse?._id) {
        const reponseArticleFusionne = [
          ...(existingReponse.reponseArticle || []).map((r) => ({
            idArticle: r.idArticle?._id || r.idArticle,
            reponses: r.reponses, // déjà avec typeReponse/choix
            remarque: r.remarque || "",
          })),
          ...nouveauxReponseArticle,
        ];

        await updateSondageReponse(existingReponse._id, {
          reponseVendeur: nouveauReponseVendeur,
          remarqueVendeur: nouvelleRemarqueVendeur,
          reponseArticle: reponseArticleFusionne,
        });
      } else {
        await createSondageReponse({
          idUser: currentUserId,
          reponseVendeur: nouveauReponseVendeur,
          remarqueVendeur: nouvelleRemarqueVendeur,
          reponseArticle: nouveauxReponseArticle,
        });
      }

      const res = await getReponseByUser(currentUserId);
      setExistingReponse(res?.data?.data || null);

      if (includeVendeur) {
        setAnswersVendeur({});
        setRemarqueVendeur("");
        setExtraQuestionsVendeur([]);
      }
      if (articleIds.length) {
        setAnswersArticles((prev) => {
          const next = { ...prev };
          articleIds.forEach((id) => delete next[id]);
          return next;
        });
        setRemarquesArticles((prev) => {
          const next = { ...prev };
          articleIds.forEach((id) => delete next[id]);
          return next;
        });
        setExtraQuestionsArticles((prev) => {
          const next = { ...prev };
          articleIds.forEach((id) => delete next[id]);
          return next;
        });
      }

      setActiveSection("all");
      await loadBoard();
    } catch (err) {
      console.error(err);
      alert("Une erreur est survenue lors de l'enregistrement.");
    } finally {
      setSavingKey(null);
    }
  };

  const handleSubmitVendeur = () =>
    submitSection("vendeur", { includeVendeur: true, articleIds: [] });

  const handleSubmitArticle = (articleId) =>
    submitSection(articleId, {
      includeVendeur: false,
      articleIds: [articleId],
    });

  const handleSubmitAll = () =>
    submitSection("all", {
      includeVendeur: needsVendorSurvey,
      articleIds: articlesEnAttente.map((a) => a._id),
    });

  // ============================================================
  // PROGRESSION
  // ============================================================

  const progress = useMemo(() => {
    if (selectedCard?.type !== "aFaire" || modalLoading) return null;
    const done =
      ((existingReponse?.reponseVendeur || []).length > 0 ? 1 : 0) +
      (existingReponse?.reponseArticle || []).length;
    const todo = (needsVendorSurvey ? 1 : 0) + articlesEnAttente.length;
    const total = done + todo;
    if (total === 0) return null;
    return { done, total, pct: Math.round((done / total) * 100) };
  }, [
    selectedCard,
    modalLoading,
    existingReponse,
    needsVendorSurvey,
    articlesEnAttente,
  ]);

  // ============================================================
  // FILTRE SIDEBAR
  // ============================================================

  const showSection = (key) => activeSection === "all" || activeSection === key;

  const selectSection = (key) => {
    setActiveSection((prev) => (prev === key ? "all" : key));
  };

  const totalRestant = (needsVendorSurvey ? 1 : 0) + articlesEnAttente.length;

  const Avatar = ({ user, className = "" }) => {
    const [imageError, setImageError] = useState(false);

    const hasPhoto =
      user?.photo && user.photo !== "/default.png" && !imageError;

    return (
      <div className={`sondage_avatar ${className}`}>
        {hasPhoto ? (
          <img
            src={user.photo}
            alt={`${user?.nom || ""} ${user?.prenom || ""}`}
            onError={() => setImageError(true)}
          />
        ) : (
          <span>{getInitiales(user)}</span>
        )}
      </div>
    );
  };

  // ============================================================
  // EDITION DES REPONSES EXISTANTES
  // ============================================================

  const startEditVendeur = () => {
    const initial = {};
    (existingReponse?.reponseVendeur || []).forEach((qa, idx) => {
      initial[idx] = qa.reponse;
    });
    setEditVendeurValues(initial);
    setEditingVendeur(true);
  };

  const cancelEditVendeur = () => {
    setEditingVendeur(false);
    setEditVendeurValues({});
  };

  const saveEditVendeur = async () => {
    if (saving || !existingReponse?._id) return;
    setSavingKey("vendeur-edit");
    try {
      const updatedReponseVendeur = existingReponse.reponseVendeur.map(
        (qa, idx) => ({
          question: qa.question,
          reponse: editVendeurValues[idx] ?? qa.reponse,
          typeReponse: Number(qa.typeReponse) || 1,
          choix: qa.choix || [],
        }),
      );

      await updateSondageReponse(existingReponse._id, {
        reponseVendeur: updatedReponseVendeur,
        remarqueVendeur: existingReponse.remarqueVendeur || "",
        reponseArticle: (existingReponse.reponseArticle || []).map((r) => ({
          idArticle: r.idArticle?._id || r.idArticle,
          reponses: r.reponses,
          remarque: r.remarque || "",
        })),
      });

      const res = await getReponseByUser(currentUserId);
      setExistingReponse(res?.data?.data || null);
      setEditingVendeur(false);
      await loadBoard();
    } catch (err) {
      console.error(err);
      alert("Erreur lors de la modification.");
    } finally {
      setSavingKey(null);
    }
  };

  const startEditArticle = (entry) => {
    const articleId = entry.idArticle?._id || entry.idArticle;
    const initial = {};
    (entry.reponses || []).forEach((qa, idx) => {
      initial[idx] = qa.reponse;
    });
    setEditArticleValues(initial);
    setEditingArticleId(articleId);
  };

  const cancelEditArticle = () => {
    setEditingArticleId(null);
    setEditArticleValues({});
  };

  const saveEditArticle = async (entry) => {
    const articleId = entry.idArticle?._id || entry.idArticle;
    if (saving || !existingReponse?._id) return;
    setSavingKey(`article-edit-${articleId}`);
    try {
      const updatedReponses = entry.reponses.map((qa, idx) => ({
        question: qa.question,
        reponse: editArticleValues[idx] ?? qa.reponse,
        typeReponse: Number(qa.typeReponse) || 1,
        choix: qa.choix || [],
      }));

      const reponseArticleFusionne = (existingReponse.reponseArticle || []).map(
        (r) => {
          const rId = r.idArticle?._id || r.idArticle;
          const reponses =
            String(rId) === String(articleId) ? updatedReponses : r.reponses;
          return { idArticle: rId, reponses, remarque: r.remarque || "" };
        },
      );

      await updateSondageReponse(existingReponse._id, {
        reponseVendeur: existingReponse.reponseVendeur || [],
        remarqueVendeur: existingReponse.remarqueVendeur || "",
        reponseArticle: reponseArticleFusionne,
      });

      const res = await getReponseByUser(currentUserId);
      setExistingReponse(res?.data?.data || null);
      setEditingArticleId(null);
      await loadBoard();
    } catch (err) {
      console.error(err);
      alert("Erreur lors de la modification.");
    } finally {
      setSavingKey(null);
    }
  };

  // ============================================================
  // RENDU
  // ============================================================

  return (
    <div className="sondage_page container-fluid">
      <div className="sondage_header d-flex align-items-center justify-content-between flex-wrap gap-2">
        <div>
          <h3 className="sondage_title mb-0">Sondages utilisateurs</h3>
          <p className="sondage_subtitle mb-0">
            Glissez d'une colonne à l'autre en répondant aux questions.
          </p>
        </div>

        <div className="d-flex align-items-center gap-2">
          <button
            type="button"
            className="btn sondage_btn_export"
            onClick={handleExportClick}
            disabled={loading || exporting || board.fait.length === 0}
          >
            <i className="bi bi-file-earmark-excel me-1" />
            {exporting ? "Export..." : "Exporter Excel"}
          </button>

          <button
            type="button"
            className="btn sondage_btn_refresh"
            onClick={loadBoard}
            disabled={loading}
          >
            <i className="bi bi-arrow-clockwise me-1" />
            {loading ? "Chargement..." : "Rafraîchir"}
          </button>
        </div>
      </div>

      {/* ---- Recherche ---- */}
      <div className="sondage_search_wrap">
        <div className="sondage_search_box">
          <i className="bi bi-search sondage_search_icon" />
          <input
            type="text"
            className="sondage_search_input"
            placeholder="Rechercher un utilisateur, un email ou une annonce…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          {search && (
            <button
              type="button"
              className="sondage_search_clear"
              onClick={() => setSearch("")}
              title="Effacer"
            >
              <i className="bi bi-x-lg" />
            </button>
          )}
        </div>
      </div>

      <div className="row sondage_board gx-3">
        <div className="sondage_mobile_switch">
          <button
            type="button"
            className={`sondage_mobile_switch_btn ${mobileView === "aFaire" ? "active" : ""}`}
            onClick={() => setMobileView("aFaire")}
          >
            <i className="bi bi-hourglass-split" />
            À faire
            <span className="sondage_mobile_switch_count">{filteredAFaire.length}</span>
          </button>
          <button
            type="button"
            className={`sondage_mobile_switch_btn ${mobileView === "fait" ? "active" : ""}`}
            onClick={() => setMobileView("fait")}
          >
            <i className="bi bi-check-circle" />
            Fait
            <span className="sondage_mobile_switch_count">{filteredFait.length}</span>
          </button>
        </div>
        {/* COLONNE NON FAIT */}
        <div className={`col-12 col-lg-6 mb-3 ${mobileView !== "aFaire" ? "sondage_col_hide_mobile" : ""}`}>
          <div className="sondage_column sondage_column_todo">
            <div className="sondage_column_header">
              <span className="sondage_column_title">
                <i className="bi bi-hourglass-split me-2" />À faire
              </span>
              <span className="sondage_column_count">
                {filteredAFaire.length}
              </span>
            </div>

            <div className="sondage_column_body">
              {loading ? (
                <div className="sondage_empty">Chargement...</div>
              ) : filteredAFaire.length === 0 ? (
                <div className="sondage_empty">
                  <i className="bi bi-emoji-smile" />
                  <p>
                    {search
                      ? "Aucun résultat pour cette recherche."
                      : "Tous les sondages sont à jour !"}
                  </p>
                </div>
              ) : (
                filteredAFaire.map((item) => {
                  const u = item.utilisateur;
                  const vendeurAFaire = Number(u?.sondageFait) !== 1;

                  return (
                    <div
                      key={u._id}
                      className="sondage_card"
                      onClick={() => openCard("aFaire", item)}
                    >
                      <div className="sondage_card_top">
                        <Avatar user={u} />

                        <div className="sondage_card_identity">
                          <div className="sondage_card_name">
                            {u?.nom} {u?.prenom}
                          </div>
                          <div className="sondage_card_meta">
                            {u?.email || getTelAffiche(u, item.articles) || "—"}
                          </div>
                        </div>
                      </div>

                      <div className="sondage_card_badges">
                        {vendeurAFaire && (
                          <span className="sondage_badge sondage_badge_vendeur">
                            <i className="bi bi-person-badge me-1" />
                            Sondage vendeur
                          </span>
                        )}

                        {item.articles.length > 0 && (
                          <span className="sondage_badge sondage_badge_article">
                            <i className="bi bi-box-seam me-1" />
                            {item.articles.length} annonce
                            {item.articles.length > 1 ? "s" : ""}
                          </span>
                        )}
                      </div>

                      <div className="sondage_card_footer">
                        <i className="bi bi-clock-history me-1" />
                        {formatRelative(item.plusAncienneDate)}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>

        {/* COLONNE FAIT */}
        <div className={`col-12 col-lg-6 mb-3 ${mobileView !== "fait" ? "sondage_col_hide_mobile" : ""}`}>
          <div className="sondage_column sondage_column_done">
            <div className="sondage_column_header">
              <span className="sondage_column_title">
                <i className="bi bi-check-circle me-2" />
                Fait
              </span>
              <span className="sondage_column_count">
                {filteredFait.length}
              </span>
            </div>

            <div className="sondage_column_body">
              {loading ? (
                <div className="sondage_empty">Chargement...</div>
              ) : filteredFait.length === 0 ? (
                <div className="sondage_empty">
                  <i className="bi bi-inbox" />
                  <p>
                    {search
                      ? "Aucun résultat pour cette recherche."
                      : "Aucun sondage complété pour l'instant."}
                  </p>
                </div>
              ) : (
                filteredFait.map((reponse) => {
                  const u = reponse.idUser;

                  return (
                    <div
                      key={reponse._id}
                      className="sondage_card sondage_card_done"
                      onClick={() => openCard("fait", reponse)}
                    >
                      <div className="sondage_card_top">
                        <Avatar user={u} className="sondage_avatar_done" />

                        <div className="sondage_card_identity">
                          <div className="sondage_card_name">
                            {u?.nom} {u?.prenom}
                          </div>
                          <div className="sondage_card_meta">
                            {u?.email ||
                              getTelAffiche(
                                u,
                                (reponse.reponseArticle || []).map(
                                  (r) => r.idArticle,
                                ),
                              ) ||
                              "—"}
                          </div>
                        </div>
                      </div>

                      <div className="sondage_card_badges">
                        <span className="sondage_badge sondage_badge_done">
                          <i className="bi bi-box-seam me-1" />
                          {(reponse.reponseArticle || []).length} annonce
                          {(reponse.reponseArticle || []).length > 1 ? "s" : ""}
                        </span>
                      </div>

                      <div className="sondage_card_footer">
                        <i className="bi bi-calendar-check me-1" />
                        Répondu le {formatDate(reponse.updatedAt)}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ====================================================== */}
      {/* MODAL */}
      {/* ====================================================== */}

      {selectedCard && (
        <div className="sondage_modal_fullscreen">
          <button
            type="button"
            className="sondage_modal_close_x"
            onClick={closeModal}
            disabled={saving}
            aria-label="Fermer"
          >
            <i className="bi bi-x-lg" />
          </button>

          {/* ============ SIDEBAR ============ */}
          <aside className="sondage_modal_sidebar">
            <div className="sondage_sidebar_user">
              <div className="sondage_avatar sondage_avatar_xl">
                {(() => {
                  const u =
                    selectedCard.type === "fait"
                      ? selectedCard.data.idUser
                      : selectedCard.data.utilisateur;

                  const initiales = getInitiales(u);

                  if (!u?.photo || u.photo == "/default.png") {
                    return <span>{initiales}</span>;
                  }

                  return (
                    <>
                      <img
                        src={u.photo}
                        alt={`${u?.nom || ""} ${u?.prenom || ""}`}
                        onError={(e) => {
                          e.currentTarget.style.display = "none";

                          const initials = e.currentTarget.nextElementSibling;
                          if (initials) {
                            initials.style.display = "flex";
                          }
                        }}
                      />

                      <span style={{ display: "none" }}>{initiales}</span>
                    </>
                  );
                })()}
              </div>

              <div>
                <div className="sondage_sidebar_name">
                  {selectedCard.type === "fait"
                    ? `${selectedCard.data.idUser?.nom || ""} ${selectedCard.data.idUser?.prenom || ""}`
                    : `${selectedCard.data.utilisateur?.nom || ""} ${selectedCard.data.utilisateur?.prenom || ""}`}
                </div>

                <div className="sondage_sidebar_email">
                  {selectedCard.type === "fait"
                    ? selectedCard.data.idUser?.email
                    : selectedCard.data.utilisateur?.email}
                </div>

                <div className="sondage_sidebar_phone">
                  {selectedCard.type === "fait"
                    ? getTelAffiche(
                      selectedCard.data.idUser,
                      (selectedCard.data.reponseArticle || []).map(
                        (r) => r.idArticle,
                      ),
                    )
                    : getTelAffiche(
                      selectedCard.data.utilisateur,
                      selectedCard.data.articles,
                    )}
                </div>

                {selectedCard.type === "aFaire" && !modalLoading && (
                  <span className="sondage_sidebar_remaining">
                    {totalRestant} restant{totalRestant > 1 ? "s" : ""}
                  </span>
                )}
              </div>
            </div>

            {progress && (
              <div className="sondage_sidebar_legend">
                <span>
                  <span className="sondage_legend_dot done" /> {progress.done}{" "}
                  répondu
                  {progress.done > 1 ? "s" : ""}
                </span>
                <span>
                  <span className="sondage_legend_dot todo" />{" "}
                  {progress.total - progress.done} restant
                  {progress.total - progress.done > 1 ? "s" : ""}
                </span>
              </div>
            )}

            {!modalLoading && (
              <nav className="sondage_sidebar_steps">
                <a
                  href="#all"
                  className={`sondage_step sondage_step_all ${activeSection === "all" ? "active" : ""
                    }`}
                  onClick={(e) => {
                    e.preventDefault();
                    setActiveSection("all");
                  }}
                >
                  <span className="sondage_step_dot">
                    <i className="bi bi-grid-fill text-white" />
                  </span>
                  Tout afficher
                </a>

                {(existingReponse?.reponseVendeur || []).length > 0 && (
                  <a
                    href="#section-vendeur-done"
                    className={`sondage_step sondage_step_done ${activeSection === "vendeur" ? "active" : ""
                      }`}
                    title="Déjà répondu"
                    onClick={(e) => {
                      e.preventDefault();
                      selectSection("vendeur");
                    }}
                  >
                    <span className="sondage_step_dot">
                      <i className="bi bi-check-lg" />
                    </span>
                    Sondage vendeur
                  </a>
                )}

                {needsVendorSurvey && (
                  <a
                    href="#section-vendeur-todo"
                    className={`sondage_step sondage_step_todo ${activeSection === "vendeur" ? "active" : ""
                      }`}
                    onClick={(e) => {
                      e.preventDefault();
                      selectSection("vendeur");
                    }}
                  >
                    <span className="sondage_step_dot" />
                    Sondage vendeur
                  </a>
                )}

                {articlesEnAttente.length > 1 && (
                  <button
                    type="button"
                    className={`sondage_step sondage_step_bulk_toggle ${bulkMode ? "active" : ""
                      }`}
                    onClick={() => {
                      setBulkMode((v) => !v);
                      setBulkSelectedIds([]);
                      setBulkAnswers({});
                    }}
                  >
                    <span className="sondage_step_dot">
                      <i className="bi bi-ui-checks-grid" />
                    </span>
                    {bulkMode
                      ? "Quitter la sélection groupée"
                      : "Répondre en groupe"}
                  </button>
                )}

                {bulkMode && (
                  <button
                    type="button"
                    className="sondage_bulk_select_all"
                    onClick={() =>
                      setBulkSelectedIds((prev) =>
                        prev.length === articlesEnAttente.length
                          ? []
                          : articlesEnAttente.map((a) => a._id),
                      )
                    }
                  >
                    {bulkSelectedIds.length === articlesEnAttente.length
                      ? "Tout désélectionner"
                      : `Tout sélectionner (${articlesEnAttente.length})`}
                  </button>
                )}

                {(existingReponse?.reponseArticle || []).map((entry, i) => {
                  const key = `article-${entry.idArticle?._id || entry.idArticle || i}`;
                  return (
                    <a
                      href={`#${key}`}
                      className={`sondage_step sondage_step_done ${activeSection === key ? "active" : ""
                        }`}
                      key={`nav-done-${i}`}
                      title="Déjà répondu"
                      onClick={(e) => {
                        e.preventDefault();
                        selectSection(key);
                      }}
                    >
                      <span className="sondage_step_dot">
                        <i className="bi bi-check-lg" />
                      </span>
                      <span className="sondage_step_label">
                        {entry.idArticle?.titre || "Annonce"}
                      </span>
                    </a>
                  );
                })}

                {articlesEnAttente.map((article) => {
                  const key = `article-${article._id}`;
                  const isChecked = bulkSelectedIds.includes(article._id);

                  if (bulkMode) {
                    return (
                      <label
                        className={`sondage_step sondage_step_todo sondage_step_checkable ${isChecked ? "checked" : ""
                          }`}
                        key={`nav-todo-${article._id}`}
                      >
                        <input
                          type="checkbox"
                          className="sondage_step_checkbox"
                          checked={isChecked}
                          onChange={() => toggleBulkArticle(article._id)}
                        />
                        <span className="sondage_step_label">
                          {article.titre}
                        </span>
                      </label>
                    );
                  }

                  return (
                    <a
                      href={`#${key}`}
                      className={`sondage_step sondage_step_todo ${activeSection === key ? "active" : ""
                        }`}
                      key={`nav-todo-${article._id}`}
                      onClick={(e) => {
                        e.preventDefault();
                        selectSection(key);
                      }}
                    >
                      <span className="sondage_step_dot" />
                      <span className="sondage_step_label">
                        {article.titre}
                      </span>
                    </a>
                  );
                })}
              </nav>
            )}

            <button
              className="sondage_sidebar_close"
              onClick={closeModal}
              disabled={saving}
            >
              <i className="bi bi-arrow-left me-2" />
              Retour au tableau
            </button>
          </aside>

          {/* ============ CONTENU ============ */}
          <div className="sondage_modal_main">
            <div className="sondage_modal_content" ref={modalContentRef}>
              {modalLoading ? (
                <div className="sondage_empty">Chargement...</div>
              ) : (
                <div className="sondage_modal_sections">
                  {/* ---- SONDAGE VENDEUR : déjà répondu ---- */}
                  {showSection("vendeur") &&
                    (existingReponse?.reponseVendeur || []).length > 0 && (
                      <section
                        id="section-vendeur-done"
                        className="sondage_form_card sondage_form_card_done"
                      >
                        <header className="sondage_form_card_header">
                          <i className="bi bi-person-check" />
                          <div>
                            <h6>Sondage vendeur</h6>
                            <span>Réponses déjà enregistrées</span>
                          </div>
                          {!editingVendeur ? (
                            <button
                              type="button"
                              className="sondage_btn_edit"
                              onClick={startEditVendeur}
                            >
                              <i className="bi bi-pencil me-1" />
                            </button>
                          ) : (
                            <div className="sondage_edit_actions">
                              <button
                                type="button"
                                className="sondage_btn_primary_sm"
                                onClick={saveEditVendeur}
                                disabled={saving}
                              >
                                {savingKey === "vendeur-edit"
                                  ? "Enregistrement..."
                                  : "Enregistrer"}
                              </button>
                              <button
                                type="button"
                                className="sondage_btn_secondary_sm"
                                onClick={cancelEditVendeur}
                                disabled={saving}
                              >
                                Annuler
                              </button>
                            </div>
                          )}
                        </header>

                        <div className="sondage_form_card_body">
                          {existingReponse.reponseVendeur.map((qa, idx) => {
                            const isChoice = Number(qa.typeReponse) === 2;

                            return (
                              <div
                                className="sondage_field sondage_field_readonly"
                                key={idx}
                              >
                                <span className="sondage_field_label">
                                  {qa.question}
                                </span>

                                {editingVendeur ? (
                                  isChoice ? (
                                    <div className="sondage_pill_group">
                                      {(qa.choix || []).map((c, i) => (
                                        <button
                                          type="button"
                                          key={i}
                                          className={`sondage_pill ${editVendeurValues[idx] === c
                                            ? "active"
                                            : ""
                                            }`}
                                          onClick={() =>
                                            setEditVendeurValues((prev) => ({
                                              ...prev,
                                              [idx]: c,
                                            }))
                                          }
                                        >
                                          {c}
                                        </button>
                                      ))}
                                    </div>
                                  ) : (
                                    <textarea
                                      className="sondage_field_input"
                                      rows={2}
                                      value={editVendeurValues[idx] ?? ""}
                                      onChange={(e) =>
                                        setEditVendeurValues((prev) => ({
                                          ...prev,
                                          [idx]: e.target.value,
                                        }))
                                      }
                                    />
                                  )
                                ) : (
                                  <div className="sondage_field_value">
                                    <i className="bi bi-check-circle-fill" />
                                    {qa.reponse || "—"}
                                  </div>
                                )}
                              </div>
                            );
                          })}

                          {existingReponse.remarqueVendeur && (
                            <div className="sondage_field sondage_field_readonly">
                              <span className="sondage_field_label">
                                Remarque
                              </span>
                              <div className="sondage_field_value_remarque">
                                <i className="bi bi-chat-left-text" />
                                {existingReponse.remarqueVendeur}
                              </div>
                            </div>
                          )}
                        </div>
                      </section>
                    )}

                  {/* ---- SONDAGE VENDEUR : à répondre ---- */}
                  {showSection("vendeur") &&
                    needsVendorSurvey &&
                    questions.questionPourVendeur.length > 0 && (
                      <section
                        id="section-vendeur-todo"
                        className="sondage_form_card sondage_form_card_todo"
                      >
                        <header className="sondage_form_card_header">
                          <i className="bi bi-person-badge" />
                          <div>
                            <h6>Sondage vendeur</h6>
                            <span>Quelques questions à propos de vous</span>
                          </div>
                        </header>

                        <div className="sondage_form_card_body">
                          {questions.questionPourVendeur.map((q, idx) => (
                            <div className="sondage_field" key={idx}>
                              <label
                                className="sondage_field_label"
                                htmlFor={`vendeur-${idx}`}
                              >
                                {q.question}
                              </label>

                              {Number(q.typeReponse) === 2 ? (
                                <div className="sondage_pill_group">
                                  {(q.choix || []).map((c, i) => (
                                    <button
                                      type="button"
                                      key={i}
                                      className={`sondage_pill ${answersVendeur[idx] === c
                                        ? "active"
                                        : ""
                                        }`}
                                      onClick={() => setReponseVendeur(idx, c)}
                                    >
                                      {c}
                                    </button>
                                  ))}
                                </div>
                              ) : (
                                <textarea
                                  id={`vendeur-${idx}`}
                                  className="sondage_field_input"
                                  rows={2}
                                  placeholder="Votre réponse..."
                                  value={answersVendeur[idx] || ""}
                                  onChange={(e) =>
                                    setReponseVendeur(idx, e.target.value)
                                  }
                                />
                              )}
                            </div>
                          ))}

                          {/* ---- Questions imprévues ---- */}
                          {extraQuestionsVendeur.length > 0 && (
                            <div className="sondage_extra_questions">
                              {extraQuestionsVendeur.map((q) => (
                                <div
                                  className="sondage_extra_question"
                                  key={q.id}
                                >
                                  <div className="sondage_extra_question_header">
                                    <span className="sondage_extra_question_tag">
                                      Question imprévue
                                    </span>
                                    <button
                                      type="button"
                                      className="sondage_extra_remove_btn"
                                      onClick={() =>
                                        removeExtraQuestionVendeur(q.id)
                                      }
                                      aria-label="Supprimer cette question"
                                    >
                                      <i className="bi bi-trash3" />
                                    </button>
                                  </div>
                                  <input
                                    type="text"
                                    className="sondage_extra_question_input"
                                    placeholder="Intitulé de la question"
                                    value={q.question}
                                    onChange={(e) =>
                                      updateExtraQuestionVendeur(
                                        q.id,
                                        "question",
                                        e.target.value,
                                      )
                                    }
                                  />
                                  <textarea
                                    className="sondage_extra_question_input"
                                    rows={2}
                                    placeholder="Réponse"
                                    value={q.reponse}
                                    onChange={(e) =>
                                      updateExtraQuestionVendeur(
                                        q.id,
                                        "reponse",
                                        e.target.value,
                                      )
                                    }
                                  />
                                </div>
                              ))}
                            </div>
                          )}

                          <button
                            type="button"
                            className="sondage_btn_add_question"
                            onClick={addExtraQuestionVendeur}
                          >
                            <i className="bi bi-plus-lg" />
                            Ajouter une question
                          </button>

                          {/* ---- Remarque ---- */}
                          <div className="sondage_remarque_block">
                            <label
                              className="sondage_remarque_label"
                              htmlFor="remarque-vendeur"
                            >
                              <i className="bi bi-chat-left-text" />
                              Remarque (optionnel)
                            </label>
                            <textarea
                              id="remarque-vendeur"
                              className="sondage_field_input"
                              rows={2}
                              placeholder="Une observation générale sur ce vendeur..."
                              value={remarqueVendeur}
                              onChange={(e) =>
                                setRemarqueVendeur(e.target.value)
                              }
                            />
                          </div>
                        </div>

                        <div className="sondage_form_card_footer">
                          <button
                            type="button"
                            className="sondage_btn_primary_sm"
                            onClick={handleSubmitVendeur}
                            // disabled={saving || !vendeurComplete}
                            disabled={saving}
                          >
                            {savingKey === "vendeur"
                              ? "Enregistrement..."
                              : "Enregistrer le sondage vendeur"}
                          </button>
                        </div>
                      </section>
                    )}

                  {/* ---- ARTICLES DEJA REPONDUS ---- */}
                  {(existingReponse?.reponseArticle || []).map((entry, i) => {
                    const articleId = entry.idArticle?._id || entry.idArticle;
                    const key = `article-${articleId || i}`;
                    if (!showSection(key)) return null;

                    const isEditingThis =
                      editingArticleId === String(articleId);

                    return (
                      <section
                        id={key}
                        className="sondage_form_card sondage_form_card_done"
                        key={i}
                      >
                        <header className="sondage_form_card_header">
                          <i className="bi bi-box-seam" />
                          <div>
                            <h6>{entry.idArticle?.titre || "Annonce"}</h6>
                            <span>Réponses déjà enregistrées</span>
                          </div>

                          {!isEditingThis ? (
                            <button
                              type="button"
                              className="sondage_btn_edit"
                              onClick={() => startEditArticle(entry)}
                            >
                              <i className="bi bi-pencil me-1" />
                              Modifier
                            </button>
                          ) : (
                            <div className="sondage_edit_actions">
                              <button
                                type="button"
                                className="sondage_btn_primary_sm"
                                onClick={() => saveEditArticle(entry)}
                                disabled={saving}
                              >
                                {savingKey === `article-edit-${articleId}`
                                  ? "Enregistrement..."
                                  : "Enregistrer"}
                              </button>
                              <button
                                type="button"
                                className="sondage_btn_secondary_sm"
                                onClick={cancelEditArticle}
                                disabled={saving}
                              >
                                Annuler
                              </button>
                            </div>
                          )}
                        </header>

                        <div className="sondage_form_card_body">
                          {(entry.reponses || []).map((qa, idx) => {
                            const isChoice = Number(qa.typeReponse) === 2;

                            return (
                              <div
                                className="sondage_field sondage_field_readonly"
                                key={idx}
                              >
                                <span className="sondage_field_label">
                                  {qa.question}
                                </span>

                                {isEditingThis ? (
                                  isChoice ? (
                                    <div className="sondage_pill_group">
                                      {(qa.choix || []).map((c, i) => (
                                        <button
                                          type="button"
                                          key={i}
                                          className={`sondage_pill ${editArticleValues[idx] === c
                                            ? "active"
                                            : ""
                                            }`}
                                          onClick={() =>
                                            setEditArticleValues((prev) => ({
                                              ...prev,
                                              [idx]: c,
                                            }))
                                          }
                                        >
                                          {c}
                                        </button>
                                      ))}
                                    </div>
                                  ) : (
                                    <textarea
                                      className="sondage_field_input"
                                      rows={2}
                                      value={editArticleValues[idx] ?? ""}
                                      onChange={(e) =>
                                        setEditArticleValues((prev) => ({
                                          ...prev,
                                          [idx]: e.target.value,
                                        }))
                                      }
                                    />
                                  )
                                ) : (
                                  <div className="sondage_field_value">
                                    <i className="bi bi-check-circle-fill" />
                                    {qa.reponse || "—"}
                                  </div>
                                )}
                              </div>
                            );
                          })}

                          {entry.remarque && (
                            <div className="sondage_field sondage_field_readonly">
                              <span className="sondage_field_label">
                                Remarque
                              </span>
                              <div className="sondage_field_value_remarque">
                                <i className="bi bi-chat-left-text" />
                                {entry.remarque}
                              </div>
                            </div>
                          )}
                        </div>
                      </section>
                    );
                  })}

                  {/* ---- ARTICLES EN ATTENTE ---- */}
                  {bulkMode && bulkSelectedIds.length > 0 && (
                    <div className="sondage_bulk_bar">
                      <div className="sondage_bulk_bar_title">
                        <i className="bi bi-check2-square" />
                        {bulkSelectedIds.length} annonce
                        {bulkSelectedIds.length > 1 ? "s" : ""} sélectionnée
                        {bulkSelectedIds.length > 1 ? "s" : ""}
                      </div>

                      {questions.questionPourArticle.map((q, idx) => (
                        <div className="sondage_field" key={idx}>
                          <label className="sondage_field_label">
                            {q.question}
                          </label>
                          {Number(q.typeReponse) === 2 ? (
                            <div className="sondage_pill_group">
                              {(q.choix || []).map((c, i) => (
                                <button
                                  type="button"
                                  key={i}
                                  className={`sondage_pill ${bulkAnswers[idx] === c ? "active" : ""
                                    }`}
                                  onClick={() =>
                                    setBulkAnswers((prev) => ({
                                      ...prev,
                                      [idx]: c,
                                    }))
                                  }
                                >
                                  {c}
                                </button>
                              ))}
                            </div>
                          ) : (
                            <textarea
                              className="sondage_field_input"
                              rows={2}
                              placeholder="Réponse à appliquer à toute la sélection..."
                              value={bulkAnswers[idx] || ""}
                              onChange={(e) =>
                                setBulkAnswers((prev) => ({
                                  ...prev,
                                  [idx]: e.target.value,
                                }))
                              }
                            />
                          )}
                        </div>
                      ))}

                      <button
                        type="button"
                        className="sondage_btn_primary_sm"
                        onClick={applyBulkAnswers}
                      >
                        Appliquer aux {bulkSelectedIds.length} annonce
                        {bulkSelectedIds.length > 1 ? "s" : ""}
                      </button>
                    </div>
                  )}

                  {/* ---- ARTICLES EN ATTENTE ---- */}
                  {articlesEnAttente.map((article) => {
                    const key = `article-${article._id}`;
                    if (!showSection(key)) return null;

                    const extraForArticle =
                      extraQuestionsArticles[article._id] || [];

                    return (
                      <section
                        id={key}
                        className="sondage_form_card sondage_form_card_todo"
                        key={article._id}
                      >
                        <header className="sondage_form_card_header">
                          <i className="bi bi-box-seam" />
                          <div>
                            <h6>{article.titre}</h6>
                            <span>Annonce à sonder</span>
                          </div>
                        </header>

                        <div className="sondage_form_card_body">
                          {questions.questionPourArticle.length === 0 ? (
                            <p className="sondage_empty_inline">
                              Aucune question configurée pour les annonces.
                            </p>
                          ) : (
                            questions.questionPourArticle.map((q, idx) => (
                              <div className="sondage_field" key={idx}>
                                <label
                                  className="sondage_field_label"
                                  htmlFor={`article-${article._id}-${idx}`}
                                >
                                  {q.question}
                                </label>

                                {Number(q.typeReponse) === 2 ? (
                                  <div className="sondage_pill_group">
                                    {(q.choix || []).map((c, i) => (
                                      <button
                                        type="button"
                                        key={i}
                                        className={`sondage_pill ${answersArticles?.[article._id]?.[
                                          idx
                                        ] === c
                                          ? "active"
                                          : ""
                                          }`}
                                        onClick={() =>
                                          setReponseArticle(article._id, idx, c)
                                        }
                                      >
                                        {c}
                                      </button>
                                    ))}
                                  </div>
                                ) : (
                                  <textarea
                                    id={`article-${article._id}-${idx}`}
                                    className="sondage_field_input"
                                    rows={2}
                                    placeholder="Votre réponse..."
                                    value={
                                      answersArticles?.[article._id]?.[idx] ||
                                      ""
                                    }
                                    onChange={(e) =>
                                      setReponseArticle(
                                        article._id,
                                        idx,
                                        e.target.value,
                                      )
                                    }
                                  />
                                )}
                              </div>
                            ))
                          )}

                          {/* ---- Questions imprévues ---- */}
                          {extraForArticle.length > 0 && (
                            <div className="sondage_extra_questions">
                              {extraForArticle.map((q) => (
                                <div
                                  className="sondage_extra_question"
                                  key={q.id}
                                >
                                  <div className="sondage_extra_question_header">
                                    <span className="sondage_extra_question_tag">
                                      Question imprévue
                                    </span>
                                    <button
                                      type="button"
                                      className="sondage_extra_remove_btn"
                                      onClick={() =>
                                        removeExtraQuestionArticle(
                                          article._id,
                                          q.id,
                                        )
                                      }
                                      aria-label="Supprimer cette question"
                                    >
                                      <i className="bi bi-trash3" />
                                    </button>
                                  </div>
                                  <input
                                    type="text"
                                    className="sondage_extra_question_input"
                                    placeholder="Intitulé de la question"
                                    value={q.question}
                                    onChange={(e) =>
                                      updateExtraQuestionArticle(
                                        article._id,
                                        q.id,
                                        "question",
                                        e.target.value,
                                      )
                                    }
                                  />
                                  <textarea
                                    className="sondage_extra_question_input"
                                    rows={2}
                                    placeholder="Réponse"
                                    value={q.reponse}
                                    onChange={(e) =>
                                      updateExtraQuestionArticle(
                                        article._id,
                                        q.id,
                                        "reponse",
                                        e.target.value,
                                      )
                                    }
                                  />
                                </div>
                              ))}
                            </div>
                          )}

                          <button
                            type="button"
                            className="sondage_btn_add_question"
                            onClick={() => addExtraQuestionArticle(article._id)}
                          >
                            <i className="bi bi-plus-lg" />
                            Ajouter une question
                          </button>

                          {/* ---- Remarque ---- */}
                          <div className="sondage_remarque_block">
                            <label
                              className="sondage_remarque_label"
                              htmlFor={`remarque-article-${article._id}`}
                            >
                              <i className="bi bi-chat-left-text" />
                              Remarque (optionnel)
                            </label>
                            <textarea
                              id={`remarque-article-${article._id}`}
                              className="sondage_field_input"
                              rows={2}
                              placeholder="Une observation sur cette annonce..."
                              value={remarquesArticles[article._id] || ""}
                              onChange={(e) =>
                                setRemarquesArticles((prev) => ({
                                  ...prev,
                                  [article._id]: e.target.value,
                                }))
                              }
                            />
                          </div>
                        </div>

                        {questions.questionPourArticle.length > 0 && (
                          <div className="sondage_form_card_footer">
                            <button
                              type="button"
                              className="sondage_btn_primary_sm"
                              onClick={() => handleSubmitArticle(article._id)}
                              // disabled={
                              //   saving || !isArticleComplete(article._id)
                              // }
                              disabled={saving}
                            >
                              {savingKey === article._id
                                ? "Enregistrement..."
                                : "Enregistrer cette annonce"}
                            </button>
                          </div>
                        )}
                      </section>
                    );
                  })}

                  {selectedCard.type === "aFaire" &&
                    !needsVendorSurvey &&
                    articlesEnAttente.length === 0 && (
                      <p className="sondage_empty_inline">
                        Rien à répondre pour cette carte.
                      </p>
                    )}
                </div>
              )}
            </div>

            {/* ============ FOOTER GLOBAL ============ */}
            {selectedCard.type === "aFaire" &&
              !modalLoading &&
              totalRestant > 0 && (
                <div className="sondage_modal_footer_fixed">
                  <button
                    type="button"
                    className="btn sondage_btn_secondary"
                    onClick={closeModal}
                    disabled={saving}
                  >
                    Annuler
                  </button>

                  <button
                    type="button"
                    className="btn sondage_btn_primary"
                    onClick={handleSubmitAll}
                    // disabled={saving || !isFormComplete}
                    disabled={saving}
                  >
                    {savingKey === "all"
                      ? "Enregistrement..."
                      : `Enregistrer tout (${totalRestant})`}
                  </button>
                </div>
              )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminSondageReponse;